/**
 * Settings Service
 * Handles all settings-related API calls
 */

import { apiService, ApiResponse } from './api.service';

/**
 * Tenant Settings Interface
 */
export interface TenantSettings {
  id: string;
  tenantId: string;
  name?: string;
  businessName: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  logo?: string;
  favicon?: string;
  primaryColor?: string;
  primaryHoverColor?: string;
  primaryLightColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  successColor?: string;
  warningColor?: string;
  dangerColor?: string;
  theme?: 'light' | 'dark';
  currency: string;
  timezone: string;
  dateFormat: string;
  taxRate: number;
  vatInclusive?: boolean; // VAT inclusive pricing
  defaultPrintFormat?: 'standard' | 'thermal'; // Default print format for receipts
  enableNotifications?: boolean;
  enableEmailNotifications: boolean;
  enableSmsNotifications: boolean;
  orderPrefix: string;
  defaultLanguage: string;
  isRestaurant?: boolean; // Is this a restaurant (enables Orders, Menus, Tables)
  isSubscriptionBased?: boolean; // Whether this business uses subscription-based customers
  loyaltyEnabled?: boolean;
  loyaltyPointsRate?: number;
  loyaltyPointsUnit?: number;
  loyaltyTierSilverMin?: number;
  loyaltyTierGoldMin?: number;
  loyaltyTierPlatinumMin?: number;
  orderByTable?: boolean; // Enable table-based ordering
  sidebarMenuVisibility?: {
    dashboard: boolean;
    orders: boolean;
    menus: boolean;
    customers: boolean;
    reviews: boolean;
    messaging: boolean;
    analytics: boolean;
    reports: boolean;
    users: boolean;
    settings: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Update Tenant Settings DTO
 */
export interface UpdateTenantSettingsDto {
  name?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  logo?: string;
  favicon?: string;
  primaryColor?: string;
  primaryHoverColor?: string;
  primaryLightColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  successColor?: string;
  warningColor?: string;
  dangerColor?: string;
  theme?: 'light' | 'dark';
  currency?: string;
  timezone?: string;
  dateFormat?: string;
  taxRate?: number;
  vatInclusive?: boolean; // VAT inclusive pricing
  defaultPrintFormat?: 'standard' | 'thermal'; // Default print format for receipts
  enableNotifications?: boolean;
  enableEmailNotifications?: boolean;
  enableSmsNotifications?: boolean;
  orderPrefix?: string;
  defaultLanguage?: string;
  isRestaurant?: boolean; // Is this a restaurant (enables Orders, Menus, Tables)
  isSubscriptionBased?: boolean; // Whether this business uses subscription-based customers
  loyaltyEnabled?: boolean;
  loyaltyPointsRate?: number;
  loyaltyPointsUnit?: number;
  loyaltyTierSilverMin?: number;
  loyaltyTierGoldMin?: number;
  loyaltyTierPlatinumMin?: number;
  orderByTable?: boolean; // Enable table-based ordering
  sidebarMenuVisibility?: {
    dashboard: boolean;
    orders: boolean;
    menus: boolean;
    customers: boolean;
    reviews: boolean;
    messaging: boolean;
    analytics: boolean;
    reports: boolean;
    users: boolean;
    settings: boolean;
  };
}

/**
 * User Preferences Interface
 */
export interface UserPreferences {
  id: string;
  userId: string;
  theme: 'light' | 'dark' | 'auto';
  language: string;
  notifications: boolean;
  emailNotifications: boolean;
  dashboardLayout: 'grid' | 'list';
  itemsPerPage: number;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Update User Preferences DTO
 */
export interface UpdateUserPreferencesDto {
  theme?: 'light' | 'dark' | 'auto';
  language?: string;
  notifications?: boolean;
  emailNotifications?: boolean;
  dashboardLayout?: 'grid' | 'list';
  itemsPerPage?: number;
  timezone?: string;
}

/**
 * System Info Interface
 */
export interface SystemInfo {
  version: string;
  environment: string;
  apiVersion: string;
  databaseStatus: 'connected' | 'disconnected';
  uptime: number;
  lastBackup?: string;
}

/**
 * Settings Service
 */
class SettingsService {
  private static readonly USER_PREFS_STORAGE_KEY = 'qrypt_user_preferences';

  private getDefaultUserPreferences(): UserPreferences {
    return {
      id: 'local-preferences',
      userId: 'current-user',
      theme: 'dark',
      language: 'en',
      notifications: true,
      emailNotifications: true,
      dashboardLayout: 'grid',
      itemsPerPage: 10,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Get tenant settings
   */
  async getTenantSettings(): Promise<ApiResponse<TenantSettings>> {
    try {
      return await apiService.get<TenantSettings>('/tenants/me/settings');
    } catch (error) {
      console.error('Failed to fetch tenant settings:', error);
      throw error;
    }
  }

  /**
   * Update tenant settings
   */
  async updateTenantSettings(data: UpdateTenantSettingsDto): Promise<ApiResponse<TenantSettings>> {
    try {
      return await apiService.patch<TenantSettings>('/tenants/me/settings', data);
    } catch (error) {
      console.error('Failed to update tenant settings:', error);
      throw error;
    }
  }

  async updateTenantBranding(data: {
    name?: string;
    logo?: string | null;
    primaryColor?: string;
    primaryHoverColor?: string;
    primaryLightColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    successColor?: string;
    warningColor?: string;
    dangerColor?: string;
  }): Promise<ApiResponse<TenantSettings>> {
    try {
      return await apiService.patch<TenantSettings>('/tenants/me/branding', data);
    } catch (error) {
      console.error('Failed to update tenant branding:', error);
      throw error;
    }
  }

  /**
   * Upload tenant logo
   */
  async uploadLogo(file: File): Promise<ApiResponse<{ url: string }>> {
    try {
      return await apiService.upload<{ url: string }>('/upload/restaurant', file, 'file');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      throw error;
    }
  }

  /**
   * Upload tenant favicon
   */
  async uploadFavicon(file: File): Promise<ApiResponse<{ url: string }>> {
    try {
      return await apiService.upload<{ url: string }>('/settings/tenant/favicon', file, 'favicon');
    } catch (error) {
      console.error('Failed to upload favicon:', error);
      throw error;
    }
  }

  /**
   * Get user preferences
   */
  async getUserPreferences(): Promise<ApiResponse<UserPreferences>> {
    try {
      const raw = localStorage.getItem(SettingsService.USER_PREFS_STORAGE_KEY);
      const fallback = this.getDefaultUserPreferences();

      if (!raw) {
        return { success: true, data: fallback };
      }

      const parsed = JSON.parse(raw) as Partial<UserPreferences>;
      return {
        success: true,
        data: {
          ...fallback,
          ...parsed,
          updatedAt: parsed.updatedAt || fallback.updatedAt,
        },
      };
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
      return { success: true, data: this.getDefaultUserPreferences() };
    }
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(data: UpdateUserPreferencesDto): Promise<ApiResponse<UserPreferences>> {
    try {
      const current = await this.getUserPreferences();
      const merged: UserPreferences = {
        ...(current.data || this.getDefaultUserPreferences()),
        ...data,
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(SettingsService.USER_PREFS_STORAGE_KEY, JSON.stringify(merged));
      return { success: true, data: merged };
    } catch (error) {
      console.error('Failed to update user preferences:', error);
      throw error;
    }
  }

  /**
   * Get system info
   */
  async getSystemInfo(): Promise<ApiResponse<SystemInfo>> {
    try {
      const health = await apiService.get<any>('/health');
      const payload: any = health.data;

      return {
        success: true,
        data: {
          version: payload?.version || '1.0.0',
          environment: payload?.environment || 'development',
          apiVersion: 'v1',
          databaseStatus: 'connected',
          uptime: Number(payload?.uptime || 0),
        },
      };
    } catch (error) {
      console.error('Failed to fetch system info:', error);

      return {
        success: true,
        data: {
          version: '1.0.0',
          environment: 'development',
          apiVersion: 'v1',
          databaseStatus: 'connected',
          uptime: 0,
        },
      };
    }
  }

  /**
   * Test email configuration
   */
  async testEmail(email: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      return await apiService.post<{ success: boolean; message: string }>('/settings/test-email', { email });
    } catch (error) {
      console.error('Failed to test email:', error);
      throw error;
    }
  }

  /**
   * Backup database
   */
  async backupDatabase(): Promise<ApiResponse<{ success: boolean; filename: string }>> {
    try {
      return await apiService.post<{ success: boolean; filename: string }>('/settings/backup');
    } catch (error) {
      console.error('Failed to backup database:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const settingsService = new SettingsService();